import { useContext, useEffect, useId, useState } from "react";
import {
  Button,
  ButtonGroup,
  ButtonToolbar,
  Form,
  FormGroup,
  Modal,
  Table,
} from "react-bootstrap";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem, Result } from "../api/backendApi";
import {
  drawingApi,
  DrawingFileModel,
  DrawingFileTypeModel,
  LocaleModel,
} from "../api/drawingApi";
import { DrawingContext } from "./DrawingContext";
import { PencilSquare, Trash } from "react-bootstrap-icons";

interface FileManagementModalProps {
  onClose: () => void;
}

function FileManagementModal({ onClose }: FileManagementModalProps) {
  const [error, setError] = useState<Problem | undefined>();
  const [loading, setLoading] = useState(false);
  const drawing = useContext(DrawingContext);
  const [files, setFiles] = useState<DrawingFileModel[]>([]);
  const [type, setType] = useState(DrawingFileTypeModel.Picture);
  const [renaming, setRenaming] = useState<DrawingFileModel>();
  const [newName, setNewName] = useState<string>();
  const renameElementId = useId();

  const reload = async () => {
    setLoading(true);
    const result = await drawingApi.getDrawingFiles(drawing.name, type);
    setLoading(false);

    if (result.ok) {
      setError(undefined);
      setFiles(result.value);
    } else {
      setError(result.problem);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const handleNameChange = async () => {
    if (!renaming?.name || !newName) return;

    setLoading(true);

    const result = await drawingApi.renameDrawingFile(
      drawing.name,
      renaming.name,
      type,
      newName,
    );

    setLoading(false);

    if (result.ok) {
      setError(undefined);
      setFiles((files) =>
        files.map((f) => (f === renaming ? { ...f, name: newName } : f)),
      );
      setRenaming(undefined);
    } else {
      setError(result.problem);
    }
  };

  const handleCleanup = async () => {
    setLoading(true);
    const result = await drawingApi.cleanupDrawingFiles(drawing.name, type);

    if (result.ok) {
      await reload();
      setError(undefined);
    } else {
      setError(result.problem);
    }
  };

  const removeDrawingFile = async (
    name: string,
    type: DrawingFileTypeModel,
    locale: LocaleModel,
  ): Promise<Result<any>> => {
    setLoading(true);

    const result = await drawingApi.removeDrawingFile(
      drawing.name,
      name,
      type,
      locale,
    );

    setLoading(false);
    return result;
  };

  const handleRemove = async (file: DrawingFileModel) => {
    const localesWithEnglishLast = file.locales.sort((a, b) =>
      a === LocaleModel.English ? 1 : b === LocaleModel.English ? -1 : 0,
    );

    for (let locale of localesWithEnglishLast) {
      const result = await removeDrawingFile(file.name, type, locale);
      if (!result.ok) {
        setError(result.problem);
        return;
      }
    }

    setError(undefined);
    setFiles((files) => files.filter((f) => f !== file));
  };

  return (
    <Modal
      show={true}
      size="lg"
      onHide={() => {
        onClose();
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>File manager</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorDisplay error={error} />
        {!renaming && (
          <>
            <ButtonToolbar>
              <ButtonGroup className="mx-2">
                <Form.Select
                  size="sm"
                  defaultValue={DrawingFileTypeModel.Picture}
                  disabled={loading}
                  onChange={(e) =>
                    e.currentTarget.value &&
                    setType(Number.parseInt(e.currentTarget.value, 10))
                  }
                >
                  <option value={DrawingFileTypeModel.Picture}>
                    Only Pictures
                  </option>
                  <option value={DrawingFileTypeModel.PDF}>Only PDFs</option>
                </Form.Select>
              </ButtonGroup>
              <div style={{ flexGrow: "1" }}></div>
              <ButtonGroup>
                <Button
                  size="sm"
                  disabled={loading || files.length === 0}
                  onClick={() => handleCleanup()}
                >
                  Remove unused{" "}
                  {type === DrawingFileTypeModel.Picture ? "pictures" : "pdfs"}
                </Button>
              </ButtonGroup>
            </ButtonToolbar>
            <hr />
            <div style={{ height: "400px", overflow: "auto" }}>
              <Table bordered={false}>
                <tbody>
                  {files.length === 0 && (
                    <tr>
                      <td colSpan={3} style={{ border: 0 }}>
                        {loading ? "Loading..." : "No files."}
                      </td>
                    </tr>
                  )}
                  {files.length > 0 &&
                    files.map((f) => (
                      <tr key={f.name}>
                        <td>{f.name}</td>
                        <td style={{ width: "1px", whiteSpace: "nowrap" }}>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="mx-1"
                            disabled={loading || renaming}
                            onClick={() => {
                              setNewName(f.name);
                              setRenaming(f);
                            }}
                          >
                            <PencilSquare className="bi" />
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="mx-1"
                            disabled={loading || renaming}
                            onClick={() => handleRemove(f)}
                          >
                            <Trash className="bi" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
        {renaming && (
          <>
            <div>
              <FormGroup controlId={renameElementId}>
                <Form.Label>New name:</Form.Label>
                <Form.Control
                  value={newName}
                  onChange={(e) => setNewName(e.currentTarget.value)}
                />
              </FormGroup>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {renaming && (
          <>
            <Button size="sm" onClick={() => handleNameChange()}>
              Rename
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setError(undefined);
                setRenaming(undefined);
              }}
            >
              Cancel
            </Button>
          </>
        )}
        {!renaming && (
          <Button variant="secondary" size="sm" onClick={() => onClose()}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default FileManagementModal;
